import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  CircularProgress,
  Tooltip,
  Divider
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import InventoryIcon from '@mui/icons-material/Inventory';
import BusinessIcon from '@mui/icons-material/Business';
import PersonIcon from '@mui/icons-material/Person';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import nameStringFn from '../../data/nameStringFn';
import FilteredAssetsFetcher from '../Assets/Myasset/FilteredAssetsFetcher';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { PDFDownloadLink } from '@react-pdf/renderer';
import UserAssetsPDFTemplate from './UserAssetsPDFTemplate';

const UserAssetsModal = ({ open, userData, onClose }) => {
  const [assetsData, setAssetsData] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  // Get current office from Redux store
  const userCurrentOffice = useSelector((state) => state.office.off);

  // Handle data fetched from FilteredAssetsFetcher
  const handleAssetsFetched = (data) => {
    console.log('Assets fetched:', data);
    setAssetsData(data || {});
    setIsLoading(false);
  };

  // Reset data when modal opens/closes
  useEffect(() => {
    if (open) {
      setIsLoading(true);
      setAssetsData({});
    } else {
      setAssetsData({});
      setIsLoading(false);
    }
  }, [open]);

  // Get user's offices (main charge and additional charge) - safe defaults if userData is null
  const userOffices = [
    userData?.office?.mainCharge,
    ...(userData?.office?.additionalCharge || [])
  ].filter(Boolean);

  // Format assets by office and store using useMemo
  const officeWiseAssets = useMemo(() => {
    if (!assetsData?.assets || !Array.isArray(assetsData.assets) || !userData) {
      return {};
    }

    // Group assets by office, then by store, then by category within each store
    return assetsData.assets.reduce((acc, asset) => {
      // Determine the office for this asset based on alloc_ofc field
      let assetOffice = asset.alloc_ofc || 'Unknown Office';
      
      // Fallback logic if alloc_ofc is not available
      if (!asset.alloc_ofc) {
        // If asset has cur_user_name and is assigned to user, try to determine office
        if (asset.cur_user_name) {
          // For individual or office assets, use the main office as fallback
          assetOffice = userData?.office?.mainCharge || 'Unknown Office';
        } 
        // If asset has cur_loc === 'office', it's a BO asset, use the office from asset
        else if (asset.cur_loc === 'office') {
          assetOffice = asset.cur_ofc || userData?.office?.mainCharge || 'Unknown Office';
        }
        // For store assets, use the main office as fallback
        else {
          assetOffice = userData?.office?.mainCharge || 'Unknown Office';
        }
      }

      // Only include assets from user's offices (main + additional charge)
      if (!userOffices.includes(assetOffice)) {
        return acc; // Skip assets not belonging to user's offices
      }

      if (!acc[assetOffice]) {
        acc[assetOffice] = {};
      }

      const storeName = asset.cur_store || 'Unknown Store';
      if (!acc[assetOffice][storeName]) {
        acc[assetOffice][storeName] = {
          individual: [],
          office: [],
          bo: [],
          store: []
        };
      }
      
      // Categorize assets within each store
      if (asset.indi_issue === true) {
        acc[assetOffice][storeName].individual.push(asset);
      }
      // Office assets (cur_loc === 'user')
      else if (asset.cur_loc === 'user') {
        acc[assetOffice][storeName].office.push(asset);
      }
      // BO assets (cur_loc === 'office')
      else if (asset.cur_loc === 'office') {
        acc[assetOffice][storeName].bo.push(asset);
      }
      // Store assets (only if user has ItemAdder_A role)
      else if (userData?.roles?.main?.includes('ItemAdder_A') || 
        Object.values(userData?.roles || {}).some(roleArray => 
          Array.isArray(roleArray) && roleArray.includes('ItemAdder_A')
        )) {
        acc[assetOffice][storeName].store.push(asset);
      }
      
      return acc;
    }, {});
  }, [assetsData, userData?.roles, userData?.office?.mainCharge, userOffices, userData]);

  // Early return after all hooks
  if (!userData) return null;

  // Check if user has BO role
  const hasBORole = userData?.roles?.main?.includes('Bo') || 
    Object.values(userData?.roles || {}).some(roleArray => 
      Array.isArray(roleArray) && roleArray.includes('Bo')
    );

  // Check if user has ItemAdder_A role
  const hasItemAdderRole = userData?.roles?.main?.includes('ItemAdder_A') || 
    Object.values(userData?.roles || {}).some(roleArray => 
      Array.isArray(roleArray) && roleArray.includes('ItemAdder_A')
    );

  // Create filters for the specific user and all their offices
  const userFilters = {
    cur_user_name: userData?.name?.firstName ? 
      `${userData.name.firstName} ${userData.name.middleName || ''} ${userData.name.lastName || ''}`.trim() : 
      userData?.email || '',
    // Filter by all user offices
    alloc_ofc: userOffices,
    // Add other relevant filters if needed
    // cur_store: [],
    // cur_loc: []
  };

  console.log('User filters:', userFilters);
  console.log('User data:', userData);
  console.log('User offices:', userOffices);
  console.log('Office-wise assets:', officeWiseAssets);
  console.log('Assets data:', assetsData);

  // Get office names with main office first, then additional offices sorted alphabetically
  const officeNames = Object.keys(officeWiseAssets).sort((a, b) => {
    const mainOffice = userData?.office?.mainCharge;
    
    // Main office comes first
    if (a === mainOffice) return -1;
    if (b === mainOffice) return 1;
    
    // Then sort additional offices alphabetically
    return a.localeCompare(b);
  });
  const totalAssets = Object.values(officeWiseAssets).reduce((officeSum, stores) => {
    return officeSum + Object.values(stores).reduce((storeSum, storeData) => {
      return storeSum + storeData.individual.length + storeData.office.length + storeData.bo.length + storeData.store.length;
    }, 0);
  }, 0);

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: {
          minHeight: '70vh',
          maxHeight: '90vh'
        }
      }}
    >
      <DialogTitle sx={{ 
        m: 0, 
        p: 2, 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        backgroundColor: '#f5f5f5'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <InventoryIcon sx={{ color: 'primary.main', fontSize: 28 }} />
          <Typography variant="h6" component="div">
            Assets Details - {nameStringFn(userData)} ({userData?.office?.mainCharge || 'Unknown Office'})
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title="Export to PDF">
            <PDFDownloadLink
              document={
                <UserAssetsPDFTemplate
                  userData={userData}
                  officeWiseAssets={officeWiseAssets}
                  userOffices={userOffices}
                />
              }
              fileName={`Assets_${nameStringFn(userData)}_${userData?.office?.mainCharge || 'Unknown'}_${moment().format('DD-MM-YYYY')}.pdf`}
            >
              {({ blob, url, loading, error }) => (
                <IconButton
                  aria-label="export"
                  disabled={loading || totalAssets === 0}
                  sx={{
                    color: (theme) => theme.palette.error.main,
                  }}
                >
                  {loading ? <CircularProgress size={20} /> : <PictureAsPdfIcon />}
                </IconButton>
              )}
            </PDFDownloadLink>
          </Tooltip>
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 3 }}>
        {/* Hidden FilteredAssetsFetcher to fetch data */}
        <FilteredAssetsFetcher 
          filters={userFilters} 
          onDataFetched={handleAssetsFetched} 
        />

        {isLoading ? (
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            py: 4,
            gap: 2
          }}>
            <CircularProgress size={60} />
            <Typography variant="h6" color="text.secondary">
              Loading Assets...
            </Typography>
          </Box>
        ) : totalAssets === 0 ? (
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            py: 4,
            gap: 2
          }}>
            <InventoryIcon sx={{ fontSize: 60, color: 'text.secondary' }} />
            <Typography variant="h6" color="text.secondary">
              No Assets Found
            </Typography>
            <Typography variant="body2" color="text.secondary" textAlign="center">
              This user doesn't have any assigned assets in any of their offices.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Office-wise Assets Sections */}
            {officeNames.map((officeName, officeIndex) => {
              const stores = officeWiseAssets[officeName];
              const storeNames = Object.keys(stores).sort();
              const officeTotal = storeNames.reduce((sum, storeName) => {
                const storeData = stores[storeName];
                return sum + storeData.individual.length + storeData.office.length + storeData.bo.length + storeData.store.length;
              }, 0);

              if (officeTotal === 0) return null;

              return (
                <Box key={officeIndex} sx={{ 
                  border: '2px solid',
                  borderColor: 'primary.main',
                  borderRadius: 2,
                  overflow: 'hidden'
                }}>
                  {/* Office Header */}
                  <Box sx={{ 
                    backgroundColor: 'primary.main', 
                    color: 'white', 
                    p: 2,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2
                  }}>
                    <BusinessIcon sx={{ fontSize: 28 }} />
                    <Typography variant="h5" component="div">
                      Office: {officeName} ({officeTotal} assets)
                    </Typography>
                  </Box>

                  {/* Store-wise Assets within Office */}
                  <Box sx={{ p: 2 }}>
                    {storeNames.map((storeName, storeIndex) => {
                      const storeData = stores[storeName];
                      const storeTotal = storeData.individual.length + storeData.office.length + storeData.bo.length + storeData.store.length;
                      
                      if (storeTotal === 0) return null;

                      return (
                        <Box key={storeIndex} sx={{ mb: 3 }}>
                          {/* Store Header */}
                          <Box sx={{ 
                            backgroundColor: 'secondary.main', 
                            color: 'white', 
                            p: 1.5,
                            borderRadius: 1,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            mb: 2
                          }}>
                            <InventoryIcon sx={{ fontSize: 20 }} />
                            <Typography variant="h6" component="div">
                              Store: {storeName} ({storeTotal} assets)
                            </Typography>
                          </Box>
                          
                          {/* Individual Assets Section */}
                          {storeData.individual.length > 0 && (
                            <Box sx={{ mb: 2 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <PersonIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                                <Typography variant="subtitle1" color="primary">
                                  Individual Assets ({storeData.individual.length})
                                </Typography>
                              </Box>
                              <TableContainer component={Paper} variant="outlined">
                                <Table size="small">
                                  <TableHead>
                                    <TableRow sx={{ backgroundColor: 'grey.100' }}>
                                      <TableCell sx={{ fontWeight: 'bold', width: '5%' }}>Sl.No.</TableCell>
                                      <TableCell sx={{ fontWeight: 'bold', width: '8%' }}>Asset Name</TableCell>
                                      <TableCell sx={{ fontWeight: 'bold', width: '8%' }}>OEM Tag</TableCell>
                                      <TableCell sx={{ fontWeight: 'bold', width: '8%' }}>OEM/Brand</TableCell>
                                      <TableCell sx={{ fontWeight: 'bold', width: '8%' }}>Model</TableCell>
                                      <TableCell sx={{ fontWeight: 'bold', width: '30%' }}>Specification</TableCell>
                                      <TableCell sx={{ fontWeight: 'bold', width: '12%' }}>Purchase Date</TableCell>
                                      <TableCell sx={{ fontWeight: 'bold', width: '12%' }}>Warranty Date</TableCell>
                                      <TableCell sx={{ fontWeight: 'bold', width: '9%' }}>Issue Date</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {storeData.individual.map((asset, index) => (
                                      <TableRow key={index} hover>
                                        <TableCell>{index + 1}</TableCell>
                                        <TableCell>{asset.name || 'N/A'}</TableCell>
                                        <TableCell>{asset.oem_tag || 'N/A'}</TableCell>
                                        <TableCell>{asset.oem || 'N/A'}</TableCell>
                                        <TableCell>{asset.model_no || 'N/A'}</TableCell>
                                        <TableCell>
                                          {asset.specification ? 
                                            Object.entries(asset.specification)
                                              .map(([key, value]) => `${key}: ${value}`)
                                              .join(', ')
                                            : 'N/A'
                                          }
                                        </TableCell>
                                        <TableCell>
                                          {asset.purchase_date ? 
                                            moment(asset.purchase_date).format("DD-MM-YYYY") : 'N/A'
                                          }
                                        </TableCell>
                                        <TableCell>
                                          {asset.warrantyexp_date ? 
                                            moment(asset.warrantyexp_date).format("DD-MM-YYYY") : 'N/A'
                                          }
                                        </TableCell>
                                        <TableCell>
                                          {asset.last_trans_date ? 
                                            moment(asset.last_trans_date).format("DD-MM-YYYY") : 'N/A'
                                          }
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </TableContainer>
                            </Box>
                          )}

                          {/* Office Assets Section */}
                          {storeData.office.length > 0 && (
                            <Box sx={{ mb: 2 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <BusinessIcon sx={{ color: 'secondary.main', fontSize: 20 }} />
                                <Typography variant="subtitle1" color="secondary">
                                  Office Assets ({storeData.office.length})
                                </Typography>
                              </Box>
                              <TableContainer component={Paper} variant="outlined">
                                <Table size="small">
                                  <TableHead>
                                    <TableRow sx={{ backgroundColor: 'grey.100' }}>
                                      <TableCell sx={{ fontWeight: 'bold', width: '5%' }}>Sl.No.</TableCell>
                                      <TableCell sx={{ fontWeight: 'bold', width: '8%' }}>Asset Name</TableCell>
                                      <TableCell sx={{ fontWeight: 'bold', width: '8%' }}>OEM Tag</TableCell>
                                      <TableCell sx={{ fontWeight: 'bold', width: '8%' }}>OEM/Brand</TableCell>
                                      <TableCell sx={{ fontWeight: 'bold', width: '8%' }}>Model</TableCell>
                                      <TableCell sx={{ fontWeight: 'bold', width: '30%' }}>Specification</TableCell>
                                      <TableCell sx={{ fontWeight: 'bold', width: '12%' }}>Purchase Date</TableCell>
                                      <TableCell sx={{ fontWeight: 'bold', width: '12%' }}>Warranty Date</TableCell>
                                      <TableCell sx={{ fontWeight: 'bold', width: '9%' }}>Issue Date</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {storeData.office.map((asset, index) => (
                                      <TableRow key={index} hover>
                                        <TableCell>{index + 1}</TableCell>
                                        <TableCell>{asset.name || 'N/A'}</TableCell>
                                        <TableCell>{asset.oem_tag || 'N/A'}</TableCell>
                                        <TableCell>{asset.oem || 'N/A'}</TableCell>
                                        <TableCell>{asset.model_no || 'N/A'}</TableCell>
                                        <TableCell>
                                          {asset.specification ? 
                                            Object.entries(asset.specification)
                                              .map(([key, value]) => `${key}: ${value}`)
                                              .join(', ')
                                            : 'N/A'
                                          }
                                        </TableCell>
                                        <TableCell>
                                          {asset.purchase_date ? 
                                            moment(asset.purchase_date).format("DD-MM-YYYY") : 'N/A'
                                          }
                                        </TableCell>
                                        <TableCell>
                                          {asset.warrantyexp_date ? 
                                            moment(asset.warrantyexp_date).format("DD-MM-YYYY") : 'N/A'
                                          }
                                        </TableCell>
                                        <TableCell>
                                          {asset.last_trans_date ? 
                                            moment(asset.last_trans_date).format("DD-MM-YYYY") : 'N/A'
                                          }
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </TableContainer>
                            </Box>
                          )}

                          {/* BO Assets Section */}
                          {storeData.bo.length > 0 && (
                            <Box sx={{ mb: 2 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <BusinessIcon sx={{ color: 'warning.main', fontSize: 20 }} />
                                <Typography variant="subtitle1" color="warning.main">
                                  BO Assets ({storeData.bo.length})
                                </Typography>
                              </Box>
                              <TableContainer component={Paper} variant="outlined">
                                <Table size="small">
                                  <TableHead>
                                    <TableRow sx={{ backgroundColor: 'grey.100' }}>
                                      <TableCell sx={{ fontWeight: 'bold', width: '5%' }}>Sl.No.</TableCell>
                                      <TableCell sx={{ fontWeight: 'bold', width: '8%' }}>Asset Name</TableCell>
                                      <TableCell sx={{ fontWeight: 'bold', width: '8%' }}>OEM Tag</TableCell>
                                      <TableCell sx={{ fontWeight: 'bold', width: '8%' }}>OEM/Brand</TableCell>
                                      <TableCell sx={{ fontWeight: 'bold', width: '8%' }}>Model</TableCell>
                                      <TableCell sx={{ fontWeight: 'bold', width: '30%' }}>Specification</TableCell>
                                      <TableCell sx={{ fontWeight: 'bold', width: '12%' }}>Purchase Date</TableCell>
                                      <TableCell sx={{ fontWeight: 'bold', width: '12%' }}>Warranty Date</TableCell>
                                      <TableCell sx={{ fontWeight: 'bold', width: '9%' }}>Issue Date</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {storeData.bo.map((asset, index) => (
                                      <TableRow key={index} hover>
                                        <TableCell>{index + 1}</TableCell>
                                        <TableCell>{asset.name || 'N/A'}</TableCell>
                                        <TableCell>{asset.oem_tag || 'N/A'}</TableCell>
                                        <TableCell>{asset.oem || 'N/A'}</TableCell>
                                        <TableCell>{asset.model_no || 'N/A'}</TableCell>
                                        <TableCell>
                                          {asset.specification ? 
                                            Object.entries(asset.specification)
                                              .map(([key, value]) => `${key}: ${value}`)
                                              .join(', ')
                                            : 'N/A'
                                          }
                                        </TableCell>
                                        <TableCell>
                                          {asset.purchase_date ? 
                                            moment(asset.purchase_date).format("DD-MM-YYYY") : 'N/A'
                                          }
                                        </TableCell>
                                        <TableCell>
                                          {asset.warrantyexp_date ? 
                                            moment(asset.warrantyexp_date).format("DD-MM-YYYY") : 'N/A'
                                          }
                                        </TableCell>
                                        <TableCell>
                                          {asset.last_trans_date ? 
                                            moment(asset.last_trans_date).format("DD-MM-YYYY") : 'N/A'
                                          }
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </TableContainer>
                            </Box>
                          )}

                          {/* Store Assets Section */}
                          {storeData.store.length > 0 && hasItemAdderRole && (
                            <Box sx={{ mb: 2 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <InventoryIcon sx={{ color: 'success.main', fontSize: 20 }} />
                                <Typography variant="subtitle1" color="success.main">
                                  Store Assets ({storeData.store.length}) - Only visible for ItemAdder_A role
                                </Typography>
                              </Box>
                              <TableContainer component={Paper} variant="outlined">
                                <Table size="small">
                                  <TableHead>
                                    <TableRow sx={{ backgroundColor: 'grey.100' }}>
                                      <TableCell sx={{ fontWeight: 'bold', width: '5%' }}>Sl.No.</TableCell>
                                      <TableCell sx={{ fontWeight: 'bold', width: '8%' }}>Asset Name</TableCell>
                                      <TableCell sx={{ fontWeight: 'bold', width: '8%' }}>OEM Tag</TableCell>
                                      <TableCell sx={{ fontWeight: 'bold', width: '8%' }}>OEM/Brand</TableCell>
                                      <TableCell sx={{ fontWeight: 'bold', width: '8%' }}>Model</TableCell>
                                      <TableCell sx={{ fontWeight: 'bold', width: '30%' }}>Specification</TableCell>
                                      <TableCell sx={{ fontWeight: 'bold', width: '12%' }}>Purchase Date</TableCell>
                                      <TableCell sx={{ fontWeight: 'bold', width: '12%' }}>Warranty Date</TableCell>
                                      <TableCell sx={{ fontWeight: 'bold', width: '9%' }}>Issue Date</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {storeData.store.map((asset, index) => (
                                      <TableRow key={index} hover>
                                        <TableCell>{index + 1}</TableCell>
                                        <TableCell>{asset.name || 'N/A'}</TableCell>
                                        <TableCell>{asset.oem_tag || 'N/A'}</TableCell>
                                        <TableCell>{asset.oem || 'N/A'}</TableCell>
                                        <TableCell>{asset.model_no || 'N/A'}</TableCell>
                                        <TableCell>
                                          {asset.specification ? 
                                            Object.entries(asset.specification)
                                              .map(([key, value]) => `${key}: ${value}`)
                                              .join(', ')
                                            : 'N/A'
                                          }
                                        </TableCell>
                                        <TableCell>
                                          {asset.purchase_date ? 
                                            moment(asset.purchase_date).format("DD-MM-YYYY") : 'N/A'
                                          }
                                        </TableCell>
                                        <TableCell>
                                          {asset.warrantyexp_date ? 
                                            moment(asset.warrantyexp_date).format("DD-MM-YYYY") : 'N/A'
                                          }
                                        </TableCell>
                                        <TableCell>
                                          {asset.last_trans_date ? 
                                            moment(asset.last_trans_date).format("DD-MM-YYYY") : 'N/A'
                                          }
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </TableContainer>
                            </Box>
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              );
            })}

            {/* Summary Section */}
            <Box sx={{ 
              mt: 2, 
              p: 2, 
              backgroundColor: 'grey.50', 
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'grey.200'
            }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Asset Summary
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {officeNames.map((officeName, officeIndex) => {
                  const stores = officeWiseAssets[officeName];
                  const storeNames = Object.keys(stores).sort();
                  const officeTotal = storeNames.reduce((sum, storeName) => {
                    const storeData = stores[storeName];
                    return sum + storeData.individual.length + storeData.office.length + storeData.bo.length + storeData.store.length;
                  }, 0);
                  
                  if (officeTotal === 0) return null;
                  
                  return (
                    <Box key={officeIndex} sx={{ 
                      p: 1, 
                      backgroundColor: 'white', 
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'grey.300'
                    }}>
                      <Typography variant="subtitle2" color="primary" gutterBottom>
                        <strong>{officeName}</strong> - Total: {officeTotal}
                      </Typography>
                      {storeNames.map((storeName, storeIndex) => {
                        const storeData = stores[storeName];
                        const storeTotal = storeData.individual.length + storeData.office.length + storeData.bo.length + storeData.store.length;
                        
                        if (storeTotal === 0) return null;
                        
                                                  return (
                            <Box key={storeIndex} sx={{ ml: 2, mb: 1 }}>
                              <Typography variant="body2" color="secondary" gutterBottom>
                                <strong>Store: {storeName}</strong> - Total: {storeTotal}
                              </Typography>
                            <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', ml: 2 }}>
                              {storeData.individual.length > 0 && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <PersonIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                                  <Typography variant="body2">
                                    Individual: <strong>{storeData.individual.length}</strong>
                                  </Typography>
                                </Box>
                              )}
                              {storeData.office.length > 0 && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <BusinessIcon sx={{ fontSize: 16, color: 'secondary.main' }} />
                                  <Typography variant="body2">
                                    Office: <strong>{storeData.office.length}</strong>
                                  </Typography>
                                </Box>
                              )}
                              {storeData.bo.length > 0 && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <BusinessIcon sx={{ fontSize: 16, color: 'warning.main' }} />
                                  <Typography variant="body2">
                                    BO: <strong>{storeData.bo.length}</strong>
                                  </Typography>
                                </Box>
                              )}
                              {storeData.store.length > 0 && hasItemAdderRole && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <InventoryIcon sx={{ fontSize: 16, color: 'success.main' }} />
                                  <Typography variant="body2">
                                    Store: <strong>{storeData.store.length}</strong>
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                  );
                })}
                <Divider sx={{ my: 1 }} />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pt: 1 }}>
                  <InventoryIcon sx={{ fontSize: 20, color: 'info.main' }} />
                  <Typography variant="body2">
                    Total Assets: <strong>{totalAssets}</strong>
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, backgroundColor: '#f5f5f5' }}>
        <Button onClick={onClose} variant="outlined">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UserAssetsModal;
